import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  Stack,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import LoadingButton from "../../../components/LoadingButton";
import TagsAutocomplete from "../../../components/TagsAutocomplete";

interface EditFolderTagsDialogProps {
  open: boolean;
  onClose: () => void;
  folder: any;
  onUpdateTags: (folderId: string, tags: string[]) => Promise<void>;
  loading: boolean;
  companyId: string;
  getToken: () => Promise<string>;
}

const EditFolderTagsDialog: React.FC<EditFolderTagsDialogProps> = ({
  open,
  onClose,
  folder,
  onUpdateTags,
  loading,
  companyId,
  getToken,
}) => {
  const [folderTags, setFolderTags] = useState<string[]>([]);

  useEffect(() => {
    if (folder) {
      // Use folderTags (the folder's own auto-apply tags)
      setFolderTags(folder.folderTags || []);
    }
  }, [folder]);

  const handleSave = async () => {
    if (folder) {
      await onUpdateTags(folder.id, folderTags);
      onClose();
    }
  };

  const handleClose = () => {
    // Reset to original folderTags
    setFolderTags(folder?.folderTags || []);
    onClose();
  };

  if (!folder) return null;

  // Child tags are tags from documents inside this folder (not the folder's own tags)
  const childTags = folder.childTags || [];
  // Filter out childTags that are already in folderTags
  const uniqueChildTags = childTags.filter(
    (tag: string) => !folderTags.includes(tag)
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "450px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Edit Folder Tags</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="subtitle1" gutterBottom>
          Folder: {folder.name}
        </Typography>

        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Folder Tags</strong> are automatically applied to all
            documents and subfolders within this folder. These tags will appear
            with a tag icon on items inside this folder.
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <TagsAutocomplete
            value={folderTags}
            onChange={setFolderTags}
            companyId={companyId}
            getToken={getToken}
            label="Folder Tags (Auto-apply)"
            placeholder="Select or add tags to auto-apply"
            disabled={loading}
          />
        </Box>

        {uniqueChildTags.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                gutterBottom
                sx={{ mb: 1 }}
              >
                <strong>Tags from documents in this folder:</strong>
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {uniqueChildTags.map((tag: string) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    variant="outlined"
                    sx={{ mb: 0.5 }}
                  />
                ))}
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                These tags are set directly on documents within this folder and
                are not controlled by folder tags.
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSave}
          loading={loading}
          variant="contained"
          startIcon={<LocalOfferIcon />}
        >
          Save Folder Tags
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditFolderTagsDialog;